<template v-if="assets && isModal">
  <div class="ProductGallery" :class="{ ProductGallery__Modal: isModal }">
    <p class="p4 ProductGallery__ReviewEyebrow" v-if="isReviewStep && !testPrice">
      {{ store.terms.steppedbundle_review.title }}
    </p>
    <Eyebrow v-if="!testPrice" class="ProductGallery__Eyebrow" :ready="store.promoReady" :item="product" :location="'pdp'" />
    <h1 v-if="!testPrice" class="t2 ProductGallery__Title">
      {{ productTitle }}
    </h1>
    <ul v-if="isBundleStep" class="ProductGallery__Steps">
      <li v-for="productStep in totalProductSteps" :key="productStep" @click="productStep.step < store.currentStep ? store.step(productStep.step) : {}" class="ProductGallery__Step t5" :class="productStep.step == store.currentStep ? 'ProductGallery__Step--Active' : ''">Step {{ productStep.step }}</li>
    </ul>
    <ReviewStars v-if="showReviews && !testPrice" :product="reviewsProduct" :is-pdp="true" />
    <p :class="[{ 'ShortDesc--withQV': location == 'quick-view' }, 'ProductGallery__ShortDesc']" v-if="shortDescription && !isReviewStep && !testPrice">
      <span class="p3" v-html="shortDescription"></span>
    </p>
    <a v-if="location == 'quick-view'" :href="url" class="ProductGallery__QVLink p4" data-color="night-sky-dark">Get All The Details. <span class="link link--bold">View Full Page</span> </a>
    <label v-if="isDropship && !testPrice" class="t4 ProductGallery__Dropship" data-type="modal" :for="`dropship-${product.handle}`">
      {{ store.terms?.modal?.spaces || 'Spaces by Brooklinen' }}
      <Icon :icon="'tooltip'" />
    </label>
    <aside class="ProductGallery__Nav" ref="nav">
      <ul class="ProductGallery__Thumbs">
        <li v-for="(asset, index) in assets" :key="asset" @click="move(index)" :class="active === index ? 'active' : ''">
          <figure :class="loaded ? 'loaded' : ''">
            <GalleryMedia :media="asset" :zoom="false" :thumbnail="true" @updateModal="updateModal" @displayMedia="displayMedia" />
            <Icon v-if="asset?.sources" :icon="'play'" />
          </figure>
        </li>
      </ul>
    </aside>
    <div class="ProductGallery__Stage" ref="stageContainer">
      <Splide :options="stageSettings" ref="stage" @splide:move="onMove" @splide:moved="onMoved" @splide:refresh="onRefresh" @splide:mounted="transitionSlider">
        <SplideSlide v-for="(asset, index) in assets" :key="asset">
          <figure :class="loaded ? 'loaded' : ''">
            <div v-if="imageBadge && index == 0" class="ProductGallery__ImageBadge">
              <picture>
                <source media="(min-width:320px)" :srcset="imageBadge" />
                <img lazy="eager" :src="imageBadge" alt="Award Badge" />
              </picture>
            </div>
            <p v-if="textBadge && index == 0" data-color="night-sky-dark" data-bg-color="white" class="ProductGallery__TextBadge t5">
              {{ textBadge }}
            </p>
            <GalleryMedia :media="asset" :thumbnail="false" @updateModal="updateModal" @displayMedia="displayMedia" :zoom="asset?.sources ? false : true" />
          </figure>
        </SplideSlide>
      </Splide>
      <button role="button" @click="move('-')" class="ProductGallery__ButtonContainer" :class="singleAssetGallery" data-direction="prev" aria-label="prev">
        <div class="btn btn--circle">
          <Icon :icon="'arrow'" />
        </div>
      </button>
      <button role="button" @click="move('+')" class="ProductGallery__ButtonContainer" :class="singleAssetGallery" data-direction="next" aria-label="next">
        <div class="btn btn--circle">
          <Icon :icon="'arrow'" />
        </div>
      </button>
    </div>
    <GalleryZoomPane v-if="zoomedMedia" :media="zoomedMedia" @updateModal="updateModal" />
  </div>
</template>

<script setup>
import Icon from '../Global/Icon.vue';
import ReviewStars from '../Global/ReviewStars.vue';
import GalleryMedia from './GalleryMedia.vue';
import GalleryZoomPane from './GalleryZoomPane.vue';
import Eyebrow from '../Global/Eyebrow.vue';
import { useProductStore } from './Composables/Store';
import { ref, computed, onMounted } from 'vue';
import { Splide, SplideSlide } from '@splidejs/vue-splide';
import { stageSettings } from './Composables/Settings';
import { initCustomAsset } from '@utils/global-functions';
import { formatValue } from '@utils/ProductResources';
import { pubSub } from '@utils/PubSub';

const active = ref(0);
const nav = ref(false);
const stageContainer = ref(false);
const stage = ref(false);
const loaded = ref(false);
const zoomedMedia = ref(false);
const store = useProductStore();

// BS-7027
const testPrice = ref(false);
//End BS-7027

const props = defineProps({
  item: { type: Object, required: false, default: false },
  title: { type: String, required: true },
  shortDescription: { type: String, required: false },
  option: { type: String, required: false, default: false },
  gallery: { type: Object, required: false, default: false },
  disableZoom: { type: Boolean, required: false, default: false },
  bundleItem: { type: Object, required: false, default: false },
  isModal: { type: Boolean, required: false, default: false },
  id: { type: String, required: false },
  url: { type: String, required: false },
  location: { type: String, required: false },
  reviewsProduct: { type: String, required: false },
});

const product = computed(() => {
  return props.location == 'quick-view' ? props.item : store.$state?.item;
});

const imageBadge = computed(() => {
  if (store.$state?.settings?.image_badge_hide) return false;
  const acfImageBadge = product.value?.imageBadge?.value ? JSON.parse(product.value?.imageBadge?.value)[0].src : false;
  const themeImageBadge = store.$state?.settings?.image_badge;
  if (themeImageBadge) {
    return themeImageBadge;
  } else if (acfImageBadge) {
    return acfImageBadge;
  } else {
    return false;
  }
});
const textBadge = computed(() => {
  if (store.$state?.settings?.text_badge_hide) return false;
  let acfTextBadge = product.value?.textBadge?.value ? JSON.parse(product.value?.textBadge?.value)[0] : false;
  const themeTextBadge = store.$state?.settings?.text_badge;
  if (themeTextBadge) {
    return themeTextBadge;
  } else if (acfTextBadge) {
    return acfTextBadge;
  } else {
    return false;
  }
});
const isDropship = computed(() => {
  const item = product.value;
  return item && item.tags.find((tag) => tag === 'Marketplace') ? true : false;
});
const productTitle = computed(() => {
  const step = props?.bundleItem?.type == 'product' ? props?.bundleItem?.step : null;
  return step ? store?.$state?.steppedCopy[`step_${step}_title`] : props.title;
});

const shortDescription = computed(() => {
  const step = props?.bundleItem?.type == 'product' ? props?.bundleItem?.step : null;
  const description = step ? store?.$state?.steppedCopy[`step_${step}_description`] : props?.shortDescription;
  return description.length > 0 ? initCustomAsset(description) : false;
});

const isReviewStep = computed(() => props.bundleItem.type == 'review');
const isBundleStep = computed(() => props.bundleItem.type == 'product' || props.bundleItem.type == 'review');
const totalProductSteps = computed(() => store?.steps?.filter((step) => step.type === 'product' || step.type === 'review'));
const showReviews = computed(() => ((!isBundleStep.value && !props.isModal && !isReviewStep.value) || props.location == 'quick-view') && store.reviewsEnabled);

const singleAssetGallery = computed(() => {
  const selectedGallery = assets.value ?? [];
  const isSingle = selectedGallery.length === 1 ?? false;

  return isSingle ? 'ProductGallery__ButtonContainer--Hidden' : '';
});

const onMove = (splide) => {
  const slider = splide.Components.Controller;
  const index = slider.getIndex();
  active.value = index;
  const prevVideo = splide.root.querySelector('.splide__slide.is-active video');
  prevVideo?.pause();
};

const onMoved = (splide) => {
  return setTimeout(() => {
    const activeVideo = splide.root.querySelector('.splide__slide.is-active:not(.splide__slide--clone) video'),
      activeVideoAutoplay = activeVideo?.getAttribute('data-autoplay') ?? false,
      activeVideoControls = splide.root.parentElement.querySelector('.ProductGallery__ButtonContainer').classList.contains('video--has-controls'),
      activeVideoArrowSpacing = activeVideo?.getAttribute('data-arrow-spacing') ?? false,
      galleryButtons = splide.root.parentElement.querySelectorAll('.ProductGallery__ButtonContainer');
    if (activeVideoAutoplay) {
      activeVideo?.play();
    }
    if (activeVideoControls) {
      galleryButtons.forEach((btn) => {
        btn.classList.remove('video--has-controls');
      });
    } else if (activeVideoArrowSpacing) {
      galleryButtons.forEach((btn) => {
        btn.classList.add('video--has-controls');
      });
    }
  }, 0);
};
const move = (direction) => {
  const slider = stage.value.splide.Components.Controller;
  return slider.go(direction);
};

const onRefresh = () => {
  const slider = stage.value.splide.Components.Controller;
  /*
   / This is absolutely not a great solution, but when the gallery loads there's
   / occasionally a bug where part of the last slide peaks into the stage.
   / Waiting, and then going to the next slide, then back to the first, resolves
   / the issue, but again, "resolves" is relative here.
   /
  */
  return setTimeout(() => {
    slider.go(1);
    slider.go(0);
    stageContainer.value.classList.add('ProductGallery__Stage--Active');
  }, 500);
};

const transitionSlider = (splide) => {
  loaded.value = false;
  splide.refresh();
};

const galleryMetaobjectSort = (asset1) => {
  return asset1.alt.includes('METAOBJECT') ? 1 : -1;
};
const getSelectedMedia = () => {
  const gallery = props.gallery;
  let option = props.option;
  const assets = gallery.filter((asset) => {
    if (asset.alt !== '' && !asset.alt.includes('FEED')) {
      let alt = asset.alt.split('[')[1].trim().split(']')[0].trim();
      return alt === option || (!asset.alt.includes('VIDEO') && asset.sources) || asset.infographic;
    }
  });
  const videos = gallery.filter((asset) => asset.sources && asset.alt.includes('VIDEO'));
  const main = assets.filter((asset) => asset.alt.includes('MAIN')).sort(galleryMetaobjectSort);
  const detail = assets.filter((asset) => asset.alt.includes('DETAIL')).sort(galleryMetaobjectSort);
  const lifestyle = assets.filter((asset) => asset.alt.includes('LIFESTYLE')).sort(galleryMetaobjectSort);
  return [...main, ...videos, ...detail, ...lifestyle];
};

const updateModal = (media) => {
  if (!props.disableZoom) zoomedMedia.value = media;
};

const displayMedia = () => setTimeout(() => (loaded.value = true), 400);
const assets = computed(() => getSelectedMedia());

// BS-7027
const listenForTestEvent = () => {
  pubSub.subscribe('pdp:price:placement', () => {
    window.Brooklinen.tests = window.Brooklinen.tests || {};
    window.Brooklinen.tests.pdpPrice = true;
    testPrice.value = true;
  });
};

onMounted(() => {
  if (window.Brooklinen?.tests?.pdpPrice) testPrice.value = true;
  listenForTestEvent();
});
// End BS-7027
</script>
